import Link from 'next/link';
import { useRouter } from 'next/router';

import { Heading, List, Modal, Text } from '@zillow/constellation-10';

import { useGetListingDetails } from '../../../hooks/queries/properties';
import propertyUtils from '../../../utils/propertyUtils';

import ListingActivationConstants from '../../../constants/ListingActivationConstants';
import { ROUTES } from '../../../constants/urls';

import type { FreemiumDatarouterError } from '../../../model/FreemiumDatarouterError';

type Props = {
  error: FreemiumDatarouterError;
  onClose: () => void;
};

const ErrorModal = ({ error, onClose }: Props) => {
  const router = useRouter();

  const listingAlias = router.query.listingAlias as string;

  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const { formattedCompleteAddress } = propertyUtils.formatAddress({
    street: listingDetails?.street,
    city: listingDetails?.city,
    state: listingDetails?.state,
    zip: listingDetails?.zip,
  });

  let errorMessage;

  let errorItems: Array<string> = [];

  switch (error.code) {
    case ListingActivationConstants.ERROR_CODE.INVALID_FIELDS:
      errorMessage = 'We found some invalid fields while activating your listing.';
      if (error.errorDetails?.data?.listing) {
        errorItems = Object.values(error.errorDetails.data.listing);
      }
      if (error.errorDetails?.data?.floorPlans) {
        errorItems.push('Fix the errors in your floor plans.');
      }
      break;
    case ListingActivationConstants.ERROR_CODE.LISTING_BLOCKED:
    case ListingActivationConstants.ERROR_CODE.CONTACT_BLOCKED:
      errorMessage = (
        <span>
          We’re unable to activate this listing at this time.
          <Link
            href={{
              pathname: ROUTES.SUBMIT_REQUEST,
              query: {
                category: 'myListing',
                topic: 'unableToPublish',
                listingAddress: formattedCompleteAddress,
                unit: listingDetails?.unit,
              },
            }}
          >
            Contact
          </Link>
          &nbsp;our team to resolve the issue.
        </span>
      );
      break;
    default:
      errorMessage = error.message;
  }

  return (
    <Modal
      header={<Heading level={2}>Activation error</Heading>}
      body={
        <>
          <Text>{errorMessage}</Text>
          {errorItems.length > 0 && (
            <List.Root>
              {errorItems.map((errorItem) => (
                <List.Item key={errorItem}>{errorItem}</List.Item>
              ))}
            </List.Root>
          )}
        </>
      }
      onOpenChange={(open) => !open && onClose()}
      open={true}
      size={{ base: 'fullScreen', md: 'md' }}
    />
  );
};

export { ErrorModal };
export default ErrorModal;
